import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

export const MODAL_ID = "folhaPagGerenciarObrasUsuario";

const LIST_URL = "/Painel/API/FolhaPag/AcessosObras/List/";
const ADD_URL = "/Painel/API/FolhaPag/AcessosObras/Add/";
const REMOVE_URL = "/Painel/API/FolhaPag/AcessosObras/Remove/";
const OBRAS_URL = "/Painel/API/GetAllObras/";

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
    position: relative;
`;

const Titulo = styled.h2`
    margin: 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitulo = styled.p`
    margin: 0;
    font-size: 13px;
    color: #64748b;
`;

const AddRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`;

const SelectObra = styled.select`
    flex: 1;
    min-width: 200px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: #fff;
    color: #1e293b;
    padding: 0 10px;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #0f766e;
        box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.2);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const AddButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    border-radius: 8px;
    background-color: #0f766e;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-sizing: border-box;

    &:hover {
        background-color: #0d6660;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const Lista = styled.div`
    width: 100%;
    max-height: 380px;
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-sizing: border-box;
`;

const ObraItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 13px;
    color: #1e293b;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: #f8fafc;
    }
`;

const ObraNome = styled.span`
    flex: 1;
    min-width: 0;
    word-break: break-word;
`;

const RemoveButton = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    color: #b91c1c;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background-color: rgba(185, 28, 28, 0.12);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Vazio = styled.div`
    padding: 28px 16px;
    text-align: center;
    color: #64748b;
    font-size: 13px;
`;

const SavingOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
`;

const ModalGerenciarObrasUsuario = ({ usuario, onChanged }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();

    const [loading, setLoading] = useState(true);
    const [loadingObras, setLoadingObras] = useState(true);
    const [saving, setSaving] = useState(false);
    const [liberadas, setLiberadas] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [selectedObra, setSelectedObra] = useState("");

    const fetchLiberadas = useCallback(async () => {
        if (!usuario?.id) return;

        setLoading(true);

        try {
            const { data } = await api.get(LIST_URL, {
                params: { user_id: usuario.id },
            });
            setLiberadas(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setLiberadas([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar obras liberadas.");
        } finally {
            setLoading(false);
        }
    }, [api, notifyError, usuario?.id]);

    const fetchCatalogo = useCallback(async () => {
        setLoadingObras(true);

        try {
            const { data } = await api.get(OBRAS_URL);
            setCatalogo(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setCatalogo([]);
            notifyError(error?.response?.data?.message || "Erro ao carregar catálogo de obras.");
        } finally {
            setLoadingObras(false);
        }
    }, [api, notifyError]);

    useEffect(() => {
        fetchLiberadas();
        fetchCatalogo();
    }, [fetchLiberadas, fetchCatalogo]);

    const liberadasSet = useMemo(
        () => new Set(liberadas.map((o) => String(o.centro_custo))),
        [liberadas]
    );

    const obrasDisponiveis = useMemo(
        () =>
            catalogo.filter(
                (o) => o?.OBRA && !liberadasSet.has(String(o.OBRA))
            ),
        [catalogo, liberadasSet]
    );

    const handleClose = () => {
        closeModal(MODAL_ID);
    };

    const handleAdd = async () => {
        if (!selectedObra || saving || !usuario?.id) return;

        setSaving(true);

        try {
            const { data } = await api.post(ADD_URL, {
                user_id: usuario.id,
                centro_custo: selectedObra,
            });

            const newItem = {
                id: data?.id,
                centro_custo: data?.centro_custo || selectedObra,
            };

            setLiberadas((prev) =>
                [...prev, newItem].sort((a, b) =>
                    String(a.centro_custo).localeCompare(String(b.centro_custo))
                )
            );
            setSelectedObra("");
            onChanged?.(usuario.id, (liberadas.length || 0) + 1);
            notifySuccess(data?.message || "Obra liberada com sucesso.");
        } catch (error) {
            console.error(error);
            notifyError(error?.response?.data?.message || "Erro ao liberar obra.");
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (obra) => {
        if (!obra?.id || saving) return;

        setSaving(true);

        try {
            const { data } = await api.post(REMOVE_URL, { id: obra.id });
            const next = liberadas.filter((o) => Number(o.id) !== Number(obra.id));
            setLiberadas(next);
            onChanged?.(usuario.id, next.length);
            notifySuccess(data?.message || "Acesso à obra removido.");
        } catch (error) {
            console.error(error);
            notifyError(error?.response?.data?.message || "Erro ao remover acesso à obra.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state changeContent={handleClose}>
            <Modal header close={handleClose} size="md">
                <Container>
                    {(loading || saving) && (
                        <SavingOverlay>
                            <SmallLoading />
                        </SavingOverlay>
                    )}

                    <div>
                        <Titulo>Obras liberadas</Titulo>
                        <Subtitulo>
                            {usuario?.usuario || usuario?.email || `Usuário #${usuario?.id}`}
                        </Subtitulo>
                    </div>

                    <AddRow>
                        <SelectObra
                            value={selectedObra}
                            disabled={saving || loadingObras || obrasDisponiveis.length === 0}
                            onChange={(e) => setSelectedObra(e.target.value)}
                            aria-label="Selecionar obra para liberar"
                        >
                            <option value="">
                                {loadingObras
                                    ? "Carregando obras..."
                                    : obrasDisponiveis.length === 0
                                      ? "Nenhuma obra disponível"
                                      : "Selecione uma obra..."}
                            </option>
                            {obrasDisponiveis.map((obra) => (
                                <option
                                    key={obra.Centro_Custo || obra.OBRA}
                                    value={obra.OBRA}
                                >
                                    {obra.OBRA}
                                </option>
                            ))}
                        </SelectObra>
                        <AddButton
                            type="button"
                            disabled={!selectedObra || saving}
                            onClick={handleAdd}
                        >
                            <FiPlus size={16} />
                            Adicionar
                        </AddButton>
                    </AddRow>

                    <Lista>
                        {loading ? (
                            <Vazio>Carregando...</Vazio>
                        ) : liberadas.length === 0 ? (
                            <Vazio>Nenhuma obra liberada para este usuário.</Vazio>
                        ) : (
                            liberadas.map((obra) => (
                                <ObraItem key={obra.id}>
                                    <ObraNome>{obra.centro_custo}</ObraNome>
                                    <RemoveButton
                                        type="button"
                                        disabled={saving}
                                        title="Remover acesso"
                                        aria-label={`Remover ${obra.centro_custo}`}
                                        onClick={() => handleRemove(obra)}
                                    >
                                        <FiTrash2 size={16} />
                                    </RemoveButton>
                                </ObraItem>
                            ))
                        )}
                    </Lista>
                </Container>
            </Modal>
        </Overlay>
    );
};

export default ModalGerenciarObrasUsuario;
